/**
 * AddressAutocomplete - Componente per autocompletamento indirizzi
 * Usa OpenStreetMap Nominatim per suggerimenti indirizzi italiani
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, Loader2, Search } from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AddressSuggestion {
  display: string;
  address: string;
  city: string;
  postal_code: string;
  province: string;
  country: string;
  lat: number;
  lon: number;
}

interface AddressData {
  address: string;
  city: string;
  postal_code: string;
  province: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (data: AddressData) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Es: Via Roma 15, Milano",
  label = "Indirizzo",
  className,
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Chiudi suggerimenti quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch suggerimenti con debounce
  const fetchSuggestions = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await axios.get(`${API}/address/autocomplete`, {
        params: { q: query }
      });
      setSuggestions(res.data.suggestions || []);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Gestione input con debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);

    // Debounce per evitare troppe richieste
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300);
  };

  // Estrae il numero civico dalla stringa dell'utente
  const extractCivico = (input: string): string => {
    // Pattern per trovare numeri civici: ", 15" o "15" o "15/A" o "15A" ecc.
    const civicoMatch = input.match(/,?\s*(\d+[\/]?[a-zA-Z]?)(?:\s|$)/);
    return civicoMatch ? civicoMatch[1] : '';
  };

  // Seleziona un suggerimento
  const handleSelectSuggestion = (suggestion: AddressSuggestion) => {
    // Estrai eventuale civico digitato dall'utente
    const userCivico = extractCivico(value);
    
    // Se l'utente ha digitato un civico ma il suggerimento non lo include, aggiungilo
    let finalAddress = suggestion.address;
    
    // Rimuovi eventuale civico esistente dal suggerimento per evitare duplicati
    const addressWithoutCivico = suggestion.address.replace(/,\s*\d+[\/]?[a-zA-Z]?$/, '').trim();
    
    if (userCivico) {
      // L'utente ha digitato un civico, usalo
      finalAddress = `${addressWithoutCivico}, ${userCivico}`;
    } else if (suggestion.address.match(/,\s*\d+[\/]?[a-zA-Z]?$/)) {
      // Il suggerimento ha già un civico, mantienilo
      finalAddress = suggestion.address;
    } else {
      // Nessun civico, usa solo la via
      finalAddress = addressWithoutCivico;
    }
    
    onChange(finalAddress);
    onAddressSelect({
      address: finalAddress,
      city: suggestion.city,
      postal_code: suggestion.postal_code,
      province: suggestion.province
    });
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Navigazione con tastiera
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      {label && <Label htmlFor="address-input">{label}</Label>}
      <div className="relative mt-1">
        <Input
          ref={inputRef}
          id="address-input"
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => value.length >= 3 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="pr-10"
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Search className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Dropdown suggerimenti */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={cn(
                "w-full px-3 py-2.5 text-left hover:bg-muted/50 flex items-start gap-3 transition-colors",
                selectedIndex === index && "bg-muted"
              )}
            >
              <MapPin className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {suggestion.address || suggestion.city}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[
                    suggestion.postal_code,
                    suggestion.city,
                    suggestion.province ? `(${suggestion.province})` : ''
                  ].filter(Boolean).join(' ')}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Messaggio nessun risultato */}
      {showSuggestions && !isLoading && value.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg p-3 text-center text-sm text-muted-foreground">
          Nessun indirizzo trovato
        </div>
      )}

      {/* Hint per iniziare */}
      {showSuggestions && value.length > 0 && value.length < 3 && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg p-3 text-center text-xs text-muted-foreground">
          Digita almeno 3 caratteri per cercare
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
